import { Box, Typography } from "@mui/material"
import type { FC } from "react"
import { useEffect } from "react"
import { useShallow } from "zustand/react/shallow"

import { useUnitOfMeasurementsStore } from "@/stores/unitOfMeasurmentsStore"
import { getUomName } from "@/utils/uom"

interface ListPriceTier {
  id: string
  qty: number
  uomId: string
  price: number
}

interface ListPriceReadOnlyProps {
  productId: number
  tiers: ListPriceTier[]
}

const formatPrice = (price: number): string =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(price)

export const ListPriceReadOnly: FC<ListPriceReadOnlyProps> = ({ productId, tiers }) => {
  const { units, getUnits } = useUnitOfMeasurementsStore(
    useShallow((state) => ({ units: state.units, getUnits: state.getUnits }))
  )

  useEffect(() => {
    if (!units.length) getUnits()
  }, [units.length, getUnits])

  if (!tiers.length) return null

  return (
    <Box
      className='flex flex-col gap-2 rounded bg-gray-100 p-4'
      data-testid={`sales.product-pricing-form.list-price-readonly-${productId}`}
    >
      <Typography className='text-base font-semibold text-gray-800'>List Price</Typography>
      <Box className='flex flex-col gap-1'>
        {tiers.map((tier) => {
          const uomLabel = getUomName(units, tier.uomId) || tier.uomId.toLowerCase()
          return (
            <Box key={tier.id} className='flex items-center gap-8 text-sm text-gray-800'>
              <Typography className='w-20 text-sm text-gray-800'>
                {tier.qty} {uomLabel}
              </Typography>
              <Typography className='text-sm text-gray-800'>{formatPrice(tier.price)}</Typography>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}
